import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, MapPin, Calendar, GraduationCap, Award } from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import { personal, timeline, education, certifications } from "../data/mock";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add("in"), io.unobserve(e.target))),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const About = () => {
  useReveal();
  return (
    <div>
      {/* Header */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-16 md:pb-24">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
            02 — About
          </div>
          <h1 className="display-xl text-5xl md:text-8xl leading-[0.95]">
            Engineer. <span className="italic font-light">Calm</span>
            <br /> systems builder.
          </h1>
          <div className="mt-12 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8 space-y-6">
              {personal.longIntro.map((p, i) => (
                <p key={i} className="text-lg md:text-xl leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              <div className="flex flex-wrap gap-3 pt-4">
                <a href={personal.resumeUrl} download className="btn-primary" data-cursor-hover>
                  <Download size={16} /> Download resume
                </a>
                <Link to="/contact" className="btn-ghost" data-cursor-hover>
                  Get in touch <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
            <div className="col-span-12 md:col-span-4">
              <div className="relative mb-6">
                <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border hairline bg-muted/40 group">
                  <img
                    src={personal.photoUrl}
                    alt="Harsh Tripathi"
                    className="w-full h-full object-cover grayscale contrast-[1.02] group-hover:grayscale-0 transition-[filter,transform] duration-700 ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.02]"
                  />
                  <div className="absolute left-3 bottom-3 px-2.5 py-1 rounded-full bg-foreground text-background font-mono text-[10px] uppercase tracking-widest">
                    {personal.role} · {personal.company}
                  </div>
                </div>
              </div>
              <div className="border hairline rounded-md divide-y hairline">
                <div className="p-5">
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-2">Based in</div>
                  <div className="flex items-center gap-2 text-sm"><MapPin size={14} /> Lucknow, India</div>
                </div>
                <div className="p-5">
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-2">Experience</div>
                  <div className="flex items-center gap-2 text-sm"><Calendar size={14} /> 3+ years · since Jan 2022</div>
                </div>
                <div className="p-5">
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-2">Currently</div>
                  <div className="flex items-center gap-2 text-sm"><GraduationCap size={14} /> Lead Engineer @ HCLTech · since Aug 2025</div>
                </div>
                <div className="p-5">
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-2">Status</div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground animate-pulse" />
                    {personal.status}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <SectionHeader
            index="02.1"
            eyebrow="Career journey"
            title="A quiet, consistent climb."
            kicker="From trainee to engineer owning business-critical services — three years of compounding small wins."
          />
          <div className="grid grid-cols-12 gap-6">
            <div className="hidden md:block md:col-span-1" />
            <div className="col-span-12 md:col-span-11 relative">
              <div className="absolute left-0 md:left-6 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-12">
                {timeline.map((t) => (
                  <div key={t.period} className="relative pl-8 md:pl-20 reveal">
                    <div className="absolute left-[-5px] md:left-[18px] top-1 w-3 h-3 rounded-full bg-foreground" />
                    <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                      {t.period}
                    </div>
                    <h3 className="font-display text-2xl md:text-4xl mt-2 leading-tight">
                      {t.role} — <span className="italic font-light">{t.company}</span>
                    </h3>
                    <div className="text-sm text-muted-foreground mt-1">{t.location}</div>
                    <ul className="mt-4 space-y-2">
                      {t.bullets.map((b, i) => (
                        <li key={i} className="flex gap-3 text-muted-foreground">
                          <span className="font-mono text-xs mt-1.5">—</span>
                          <span className="leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education + Certifications */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6 reveal">
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
              — 02.2 Education
            </div>
            <h3 className="display-xl text-3xl md:text-5xl mb-10">Learning, on loop.</h3>
            <div className="divide-y hairline border-t border-b hairline">
              {education.map((e) => (
                <div key={e.degree} className="py-6 flex items-start justify-between gap-6">
                  <div>
                    <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
                      {e.period}
                    </div>
                    <div className="font-display text-xl md:text-2xl mt-1">{e.degree}</div>
                    <div className="text-sm text-muted-foreground mt-1">{e.school}</div>
                  </div>
                  <div className="font-mono text-xs text-muted-foreground whitespace-nowrap">{e.note}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 reveal">
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-5">
              — 02.3 Certifications
            </div>
            <h3 className="display-xl text-3xl md:text-5xl mb-10">Credentials.</h3>
            <div className="grid gap-4">
              {certifications.map((c) => (
                <div
                  key={c.name}
                  className="p-5 border hairline rounded-md flex items-start gap-4 hover:bg-muted/40 transition-colors"
                  data-cursor-hover
                >
                  <div className="w-10 h-10 rounded-md border hairline grid place-items-center">
                    <Award size={16} />
                  </div>
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{c.issuer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
